// Cards repository — talks to the dedicated `public.cards` table.
//
// History: cards used to live inside the single `app_state` blob row. They
// were promoted to their own table to make individual cards inspectable in
// the Supabase dashboard, to enable future per-field columns, and to set us
// up for a future Unreal Engine port where the engine reads structured rows.
// The table currently stores each card as a single `data jsonb` column —
// shape-identical to the in-memory Card object — so no schema commitment
// is made today. Specific columns can be promoted later as patterns reveal
// themselves to be data-driven.
//
// See `src/lib/store.ts` for how this repo is wired into load/save.

import { supabase } from "./supabase";
import type { Card } from "@/types/card";

interface CardRow {
  id: string;
  data: Card;
}

/** Load every card from the dedicated table. Returns an empty array on error. */
export async function listCards(): Promise<Card[]> {
  const { data, error } = await supabase
    .from("cards")
    .select("data")
    .order("created_at", { ascending: true });
  if (error) {
    console.error("[cardsRepo.listCards]", error);
    return [];
  }
  return ((data ?? []) as CardRow[]).map((row) => row.data);
}

/** Upsert a single card. Throws on error so callers can react. */
export async function upsertCard(card: Card): Promise<void> {
  const { error } = await supabase.from("cards").upsert(
    {
      id: card.id,
      data: card,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "id" },
  );
  if (error) throw error;
}

/** Delete a card by id. */
export async function deleteCard(id: string): Promise<void> {
  const { error } = await supabase.from("cards").delete().eq("id", id);
  if (error) throw error;
}

/**
 * Full-collection sync: upsert every supplied card, then delete any rows in
 * the table that aren't in the supplied list. Used by saveAppState so the
 * existing setState-based store API still works without per-card writes.
 *
 * Trade-off: rewrites all rows on every save. At ~70 cards this is fine; if
 * the catalog grows large, swap to per-card writes in CardForm.tsx and stop
 * calling syncCards from saveAppState.
 */
export async function syncCards(cards: Card[]): Promise<void> {
  if (cards.length > 0) {
    const now = new Date().toISOString();
    const rows = cards.map((c) => ({
      id: c.id,
      data: c,
      updated_at: now,
    }));
    const { error } = await supabase
      .from("cards")
      .upsert(rows, { onConflict: "id" });
    if (error) throw error;
  }

  const { data: existing, error: listErr } = await supabase
    .from("cards")
    .select("id");
  if (listErr) throw listErr;

  const currentIds = new Set(cards.map((c) => c.id));
  const toDelete = (existing ?? [])
    .map((r) => r.id as string)
    .filter((id) => !currentIds.has(id));
  if (toDelete.length > 0) {
    const { error } = await supabase
      .from("cards")
      .delete()
      .in("id", toDelete);
    if (error) throw error;
  }
}
